import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import client from '../Contentful_API/api';
import { PageHero } from '../Common/PageLayout';
import { LoadingState } from '../Common/PageLayout';
import { tokens } from '../../theme';

const useStyles = makeStyles((theme) => ({
  section: {
    padding: theme.spacing(7, 0, 9),
    backgroundColor: tokens.surface,
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(5, 0, 6),
    },
  },
  intro: {
    maxWidth: 700,
    marginBottom: theme.spacing(5),
  },
  overline: {
    display: 'block',
    marginBottom: theme.spacing(1),
    color: tokens.blue600,
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
  },
  introTitle: {
    marginBottom: theme.spacing(1.5),
  },
  introText: {
    margin: 0,
    color: tokens.inkSoft,
    fontSize: '1.0625rem',
    lineHeight: 1.7,
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 220,
    padding: theme.spacing(3.5),
    border: `1px solid ${tokens.border}`,
    borderTop: `4px solid ${tokens.blue600}`,
    borderRadius: tokens.radius,
    backgroundColor: tokens.white,
    color: tokens.ink,
    textDecoration: 'none',
    boxShadow: tokens.shadowSm,
    transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
    '&:hover': {
      borderTopColor: tokens.gold,
      boxShadow: tokens.shadowMd,
      transform: 'translateY(-3px)',
    },
    '&:focus-visible': {
      outline: `3px solid ${tokens.gold}`,
      outlineOffset: 3,
    },
  },
  icon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 46,
    height: 46,
    marginBottom: theme.spacing(2.5),
    borderRadius: 6,
    backgroundColor: tokens.blue50,
    color: tokens.blue600,
  },
  cardTitle: {
    marginBottom: theme.spacing(1),
    color: tokens.ink,
  },
  cardText: {
    flexGrow: 1,
    margin: 0,
    color: tokens.muted,
    fontSize: '0.9375rem',
    lineHeight: 1.6,
  },
  action: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginTop: theme.spacing(2.5),
    color: tokens.blue600,
    fontSize: '0.875rem',
    fontWeight: 700,
  },
}));

function getDescription(title = '') {
  const normalizedTitle = title.toLowerCase();
  if (normalizedTitle.includes('tender')) {
    return 'View current opportunities, notices and requests published by the Ministry.';
  }
  if (normalizedTitle.includes('portal')) {
    return 'Access the national platform for supplier registration and electronic submissions.';
  }
  if (normalizedTitle.includes('document')) {
    return 'Find official procurement documents, procedures and supporting information.';
  }
  if (normalizedTitle.includes('guide')) {
    return 'Review practical guidance for participating in government procurement.';
  }
  return 'Explore official information and services related to public procurement.';
}

function ProcurementCard({ menu }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const title = menu.fields?.title || 'Procurement resource';

  return (
    <Link className={classes.card} to={`/pagedetails/${menu.fields?.slug}`}>
      <span className={classes.icon} aria-hidden="true">
        <DescriptionOutlinedIcon />
      </span>
      <Typography variant="h5" component="h2" className={classes.cardTitle}>
        {title}
      </Typography>
      <p className={classes.cardText}>{getDescription(title)}</p>
      <span className={classes.action}>
        {t('Open resource')} <ArrowForwardIcon fontSize="small" />
      </span>
    </Link>
  );
}

export default function ProcurementPage() {
  const classes = useStyles();
  const { t } = useTranslation();
  const [menus, setMenus] = useState(null);

  useEffect(() => {
    let active = true;
    client
      .getEntries({
        content_type: 'menus',
        'fields.type': 'procurement',
        order: 'fields.menuorder',
      })
      .then((response) => {
        if (active) setMenus(response.items || []);
      })
      .catch(() => {
        if (active) setMenus([]);
      });

    return () => {
      active = false;
    };
  }, []);

  if (menus === null) return <LoadingState label="Loading procurement information" />;

  return (
    <>
      <PageHero
        eyebrow={t('Procurement')}
        title={t('Public procurement')}
        description={t('Official resources for suppliers, contractors and members of the public.')}
        breadcrumbs={[{ label: t('Procurement') }]}
      />

      <main className={classes.section}>
        <Container maxWidth="lg">
          <div className={classes.intro}>
            <span className={classes.overline}>{t('Find what you need')}</span>
            <Typography variant="h2" component="h2" className={classes.introTitle}>
              {t('Procurement resources')}
            </Typography>
            <p className={classes.introText}>
              {t('Browse tender information, official documents, practical guidance and online procurement services from the Ministry of Finance.')}
            </p>
          </div>

          {menus.length > 0 ? (
            <Grid container spacing={3}>
              {menus.map((menu) => (
                <Grid item xs={12} sm={6} md={4} key={menu.sys?.id || menu.fields?.slug}>
                  <ProcurementCard menu={menu} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography color="textSecondary">{t('No procurement resources are available at the moment.')}</Typography>
          )}
        </Container>
      </main>
    </>
  );
}


